import React from 'react';
import { TextField, Box } from '@mui/material';
import { GoogleAnalytics } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import {
	isValidGA4AnalyticsTag,
	isValidGtmTag,
	isValidUAAnalyticsTag,
} from 'common/utils/validation';

interface Props {
	gaTags: GoogleAnalytics;
	setGaTags: (googleAnalytics: GoogleAnalytics) => void;
}

const UA_TAG_INPUT = 'UA_TAG_INPUT';
const GA4_TAG_INPUT = 'GA4_TAG_INPUT';

const AnalyticsTagInput = (props: Props) => {
	const tags = props.gaTags ?? {};
	const uaTag = tags.UA?.[0] ?? '';
	const ga4Tag = tags.GA4?.[0] ?? '';
	const gtmTag = tags.GTM?.[0] ?? '';

	const isGA4TagValid = !ga4Tag ? true : isValidGA4AnalyticsTag(ga4Tag);
	const isUATagValid = !uaTag ? true : isValidUAAnalyticsTag(uaTag);
	const isGtmValid = !gtmTag ? true : isValidGtmTag(gtmTag);

	const { t } = useTranslation();

	const handleGA4TagChange = (event: React.ChangeEvent<{ value: string }>) => {
		const { value } = event.target;

		props.setGaTags({
			...props.gaTags,
			GA4: value === '' ? [] : [value],
		});
	};

	const handleUATagChange = (event: React.ChangeEvent<{ value: string }>) => {
		const { value } = event.target;
		props.setGaTags({
			...props.gaTags,
			UA: value === '' ? [] : [value],
		});
	};

	const handleGtmTagChange = (event: React.ChangeEvent<{ value: string }>) => {
		const { value } = event.target;
		props.setGaTags({
			...props.gaTags,
			GTM: value === '' ? [] : [value],
		});
	};

	const gtmCookieNote = t(
		'Analytics.gtmCookieNote',
		'Note: Rentle cannot control website cookies added via the GTM container. You might need to use an external service to ensure user consent to cookie tracking',
	);

	return (
		<Box>
			<TextField
				defaultValue={props.gaTags?.GA4}
				placeholder="G-XXXXXXX"
				label="GA4 Measurement ID"
				InputLabelProps={{
					shrink: true,
				}}
				multiline={false}
				fullWidth
				variant="outlined"
				name={GA4_TAG_INPUT}
				error={!isGA4TagValid && ga4Tag !== ''}
				helperText={
					!isGA4TagValid && ga4Tag !== ''
						? t('Analytics.error', {
								format: 'G-XXXXXXX',
								defaultValue: 'Not a valid analytics ID ({{format}})',
						  })
						: ''
				}
				onChange={(event) => handleGA4TagChange(event)}
				sx={{ mb: 2 }}
			/>
			<TextField
				defaultValue={props.gaTags?.UA}
				placeholder="UA-XXXXXX-X"
				label="Universal Analytics Tracking ID"
				InputLabelProps={{
					shrink: true,
				}}
				multiline={false}
				fullWidth
				variant="outlined"
				name={UA_TAG_INPUT}
				error={!isUATagValid && uaTag !== ''}
				helperText={
					!isUATagValid && uaTag !== ''
						? t('Analytics.error', {
								format: 'UA-XXXXXX-X',
								defaultValue: 'Not a valid analytics ID ({{format}})',
						  })
						: ''
				}
				onChange={(event) => handleUATagChange(event)}
				sx={{ mb: 2 }}
			/>
			<TextField
				defaultValue={props.gaTags?.GTM}
				placeholder="GTM-XXXXXX"
				label="Google Tag Manager ID"
				InputLabelProps={{
					shrink: true,
				}}
				multiline={false}
				fullWidth
				variant="outlined"
				error={!isGtmValid && gtmTag !== ''}
				helperText={
					!isGtmValid && gtmTag !== ''
						? t('Analytics.error', {
								format: 'GTM-XXXXXX',
								defaultValue: 'Not a valid analytics ID ({{format}})',
						  })
						: gtmCookieNote
				}
				onChange={(event) => handleGtmTagChange(event)}
			/>
		</Box>
	);
};

export default AnalyticsTagInput;
